import {
    useSyncExternalStore
} from "react";
import {
    Platform
} from "react-native";
import type {
    KbdAccessibilityLabelConstantType,
    KbdSeparatorConstantType,
    KbdResolveConstantType,
    KbdNamedKeyDefinition,
    KbdParseConstantType,
    KbdNavigatorType,
    KbdResolvedKey,
    KbdNamedKey,
    KbdPlatform
} from "./type";

const APPLE_PLATFORM_PATTERN = /\b(mac|iphone|ipad|ipod|ios)/i;

const TOUCH_ONLY_QUERY = "(pointer: coarse) and (not (any-pointer: fine))";

const KEYBOARD_PROBE_KEYS = [
    "Control",
    "Meta",
    "Alt",
    "Tab"
];

const keyboardListeners = new Set<() => void>();

let isKeyboardDetected = false;

export const KBD_NAMED_KEYS: Record<KbdNamedKey, KbdNamedKeyDefinition> = {
    ctrl: {
        apple: {
            name: "kbd-control",
            label: "⌃"
        },
        other: {
            name: "kbd-control",
            label: "Ctrl"
        }
    },
    mod: {
        apple: {
            name: "kbd-command",
            label: "⌘"
        },
        other: {
            name: "kbd-control",
            label: "Ctrl"
        }
    },
    alt: {
        apple: {
            name: "kbd-option",
            label: "⌥"
        },
        other: {
            label: "Alt"
        }
    },
    escape: {
        apple: {
            name: "kbd-escape",
            label: "Esc"
        },
        other: {
            name: "kbd-escape",
            label: "Esc"
        }
    },
    enter: {
        apple: {
            name: "kbd-enter",
            label: "↵"
        },
        other: {
            label: "Enter"
        }
    },
    shift: {
        apple: {
            name: "kbd-shift",
            label: "⇧"
        },
        other: {
            label: "Shift"
        }
    }
};

export const KBD_KEY_ALIASES = new Map<string, KbdNamedKey>([
    [
        "control",
        "ctrl"
    ],
    [
        "return",
        "enter"
    ],
    [
        "option",
        "alt"
    ],
    [
        "esc",
        "escape"
    ]
]);

export const KBD_LITERAL_NAMES = new Map<string, keyof NCoreUIKit.Translation>([
    [
        "⌘",
        "kbd-command"
    ],
    [
        "⌃",
        "kbd-control"
    ],
    [
        "⌥",
        "kbd-option"
    ],
    [
        "⇧",
        "kbd-shift"
    ],
    [
        "↵",
        "kbd-enter"
    ],
    [
        ",",
        "kbd-comma"
    ]
]);

const KBD_NAMED_KEY_SET = new Set<string>(Object.keys(KBD_NAMED_KEYS));

const isKbdNamedKey = (key: string): key is KbdNamedKey => KBD_NAMED_KEY_SET.has(key);

export const getKbdPlatform = (): KbdPlatform => {
    if(Platform.OS === "ios" || Platform.OS === "macos") {
        return "apple";
    }

    if(Platform.OS !== "web") {
        return "other";
    }

    const currentNavigator: KbdNavigatorType | undefined = globalThis.navigator;

    const platformHint = currentNavigator?.userAgentData?.platform || currentNavigator?.platform || currentNavigator?.userAgent || "";

    return APPLE_PLATFORM_PATTERN.test(platformHint) ? "apple" : "other";
};

export const parseKbdKeys = ({
    keys
}: KbdParseConstantType): Array<string> => {
    if(typeof keys !== "string") {
        return keys.filter((key) => key.trim());
    }

    const isEndingWithPlus = keys.trim() === "+" || keys.trimEnd().endsWith("++");

    const parsedKeys = (isEndingWithPlus ? keys.trimEnd().slice(0, -1) : keys)
        .split("+")
        .map((key) => key.trim())
        .filter((key) => key);

    return isEndingWithPlus ? [
        ...parsedKeys,
        "+"
    ] : parsedKeys;
};

export const resolveKbdKey = ({
    localize,
    platform,
    index,
    key
}: KbdResolveConstantType): KbdResolvedKey => {
    const trimmedKey = key.trim();

    const lowerKey = trimmedKey.toLowerCase();

    const namedKey = KBD_KEY_ALIASES.get(lowerKey) ?? lowerKey;

    if(isKbdNamedKey(namedKey)) {
        const definition = KBD_NAMED_KEYS[namedKey][platform];

        return {
            name: definition.name ? localize(definition.name) : undefined,
            id: `${index}-${namedKey}`,
            label: definition.label
        };
    }

    const label = Array.from(trimmedKey).length === 1 ? trimmedKey.toUpperCase() : trimmedKey;

    const literalName = KBD_LITERAL_NAMES.get(label);

    return {
        name: literalName ? localize(literalName) : undefined,
        id: `${index}-${label}`,
        label
    };
};

export const getKbdSeparatorVisibility = ({
    separator,
    platform
}: KbdSeparatorConstantType): boolean => {
    if(separator === "auto") {
        return platform === "other";
    }

    return separator === "plus";
};

export const getKbdAccessibilityLabel = ({
    isShowSeparator,
    keys
}: KbdAccessibilityLabelConstantType): string => {
    return keys.map((key) => key.name ?? key.label).join(isShowSeparator ? " + " : " ");
};

const onKeyboardProbe = (event: KeyboardEvent) => {
    if(!KEYBOARD_PROBE_KEYS.includes(event.key)) {
        return;
    }

    isKeyboardDetected = true;

    globalThis.removeEventListener("keydown", onKeyboardProbe, true);

    keyboardListeners.forEach((listener) => {
        listener();
    });
};

const subscribeTouchOnly = (listener: () => void) => {
    if(Platform.OS !== "web" || typeof globalThis.matchMedia !== "function") {
        return () => {};
    }

    const query = globalThis.matchMedia(TOUCH_ONLY_QUERY);

    query.addEventListener?.("change", listener);

    if(!keyboardListeners.size && !isKeyboardDetected) {
        globalThis.addEventListener("keydown", onKeyboardProbe, true);
    }

    keyboardListeners.add(listener);

    return () => {
        query.removeEventListener?.("change", listener);

        keyboardListeners.delete(listener);

        if(!keyboardListeners.size) {
            globalThis.removeEventListener("keydown", onKeyboardProbe, true);
        }
    };
};

const getTouchOnlySnapshot = (): boolean => {
    if(Platform.OS !== "web") {
        return Platform.OS === "ios" || Platform.OS === "android";
    }

    if(isKeyboardDetected || typeof globalThis.matchMedia !== "function") {
        return false;
    }

    return globalThis.matchMedia(TOUCH_ONLY_QUERY).matches;
};

const getServerTouchOnlySnapshot = (): boolean => false;

export const useKbdTouchOnly = (): boolean => {
    return useSyncExternalStore(subscribeTouchOnly, getTouchOnlySnapshot, getServerTouchOnlySnapshot);
};
